import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  EmojiEvents as BonusIcon,
} from "@mui/icons-material";
import { Player, Division } from "../types";
import { apiRequest } from "../config/api";
import { useDivision } from "../contexts/DivisionContext";
import { useSeason } from "../contexts/SeasonContext";
import DivisionSelector from "../components/DivisionSelector";
import SeasonSelector from "../components/SeasonSelector";

interface PenaltyOrBonus {
  id: string;
  playerId: string;
  playerName: string;
  division: Division;
  points: number;
  reason?: string;
  disqualificationReason?: string;
  createdAt: Date;
  isDisqualification?: boolean;
  type: "penalty" | "bonus";
  leagueDayAdjustment?: number; // +N for penalty days, -N for bonus days
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function PenaltiesAndBonuses() {
  const { currentDivision } = useDivision();
  const { selectedSeason } = useSeason();
  const [tabValue, setTabValue] = useState(0);
  const [players, setPlayers] = useState<Player[]>([]);
  const [penalties, setPenalties] = useState<PenaltyOrBonus[]>([]);
  const [bonuses, setBonuses] = useState<PenaltyOrBonus[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"penalty" | "bonus">("penalty");
  const [selectedPlayer, setSelectedPlayer] = useState<string>("");
  const [selectedDivision, setSelectedDivision] =
    useState<Division>("Division 1");
  const [availableDivisions, setAvailableDivisions] = useState<Division[]>([]);
  const [pointsOption, setPointsOption] = useState<"preset" | "custom">(
    "preset",
  );
  const [presetPoints, setPresetPoints] = useState<number>(3);
  const [customPoints, setCustomPoints] = useState<string>("");
  const [reason, setReason] = useState("");
  const [leagueDayAdjustment, setLeagueDayAdjustment] = useState<string>("");
  const [isDisqualification, setIsDisqualification] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchData();
  }, [currentDivision, selectedSeason]);

  // Update available divisions when player is selected
  useEffect(() => {
    const fetchPlayerDivisions = async () => {
      if (!selectedPlayer) {
        setAvailableDivisions([]);
        return;
      }

      try {
        const response = await apiRequest(`/api/players/${selectedPlayer}`);
        if (response.ok) {
          const playerData = await response.json();
          const divisions: Division[] = [];

          if (playerData.division) {
            divisions.push(playerData.division);
            setSelectedDivision(playerData.division);
          }

          if (playerData.divisionStats) {
            Object.keys(playerData.divisionStats).forEach((div) => {
              if (!divisions.includes(div as Division)) {
                divisions.push(div as Division);
              }
            });
          }

          setAvailableDivisions(divisions);
        }
      } catch (error) {
        console.error("Error fetching player divisions:", error);
      }
    };

    fetchPlayerDivisions();
  }, [selectedPlayer]);

  const fetchData = async () => {
    if (!selectedSeason) return;

    setLoading(true);
    try {
      const playersResponse = await apiRequest(`/api/players`);
      if (playersResponse.ok) {
        const playersData = await playersResponse.json();
        setPlayers(playersData.filter((p: Player) => p.isActive));
      }

      const params = new URLSearchParams({
        seasonId: selectedSeason.id,
      });
      const penaltiesResponse = await apiRequest(
        `/api/penalties?${params.toString()}`,
      );
      if (penaltiesResponse.ok) {
        const data = await penaltiesResponse.json();
        const filteredPenalties = data.filter((p: PenaltyOrBonus) => p.type === "penalty" || !p.type);
        const filteredBonuses = data.filter((p: PenaltyOrBonus) => p.type === "bonus");
        setPenalties(filteredPenalties);
        setBonuses(filteredBonuses);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data");
      setLoading(false);
    }
  };

  const handleAddPenaltyOrBonus = async () => {
    if (!selectedPlayer) {
      setError("Please select a player");
      return;
    }

    if (
      dialogType === "penalty" &&
      isDisqualification &&
      !disqualificationReason.trim()
    ) {
      setError("Please provide a reason for disqualification");
      return;
    }

    if (!isDisqualification && !reason.trim()) {
      setError("Please provide a reason");
      return;
    }

    // Check if at least points or league day adjustment is provided
    const leagueDayAdj = leagueDayAdjustment.trim()
      ? parseInt(leagueDayAdjustment)
      : 0;
    const finalPoints =
      pointsOption === "custom" ? parseInt(customPoints) : presetPoints;

    if (!isDisqualification && finalPoints === 0 && leagueDayAdj === 0) {
      setError("Please provide either points or league day adjustment");
      return;
    }

    if (
      pointsOption === "custom" &&
      customPoints.trim() &&
      (isNaN(finalPoints) || finalPoints < 0)
    ) {
      setError("Please enter a valid positive number for points");
      return;
    }

    try {
      const response = await apiRequest(`/api/penalties`, {
        method: "POST",
        body: JSON.stringify({
          playerId: selectedPlayer,
          points: isDisqualification ? 0 : finalPoints,
          division: selectedDivision,
          reason: reason.trim(),
          isDisqualification,
          disqualificationReason: isDisqualification
            ? disqualificationReason.trim()
            : undefined,
          type: dialogType,
          leagueDayAdjustment: isDisqualification ? 0 : leagueDayAdj,
        }),
      });

      if (response.ok) {
        const action = isDisqualification
          ? "disqualified"
          : dialogType === "bonus"
            ? "bonus awarded"
            : "penalty added";
        setSuccess(`Player ${action} successfully`);
        setDialogOpen(false);
        resetDialogState();
        fetchData();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const errorData = await response.json();
        setError(
          errorData.error ||
            `Failed to ${dialogType === "bonus" ? "add bonus" : "add penalty"}`,
        );
      }
    } catch (error) {
      console.error(`Error adding ${dialogType}:`, error);
      setError(
        `Failed to ${dialogType === "bonus" ? "add bonus" : "add penalty"}`,
      );
    }
  };

  const resetDialogState = () => {
    setSelectedPlayer("");
    setSelectedDivision("Division 1");
    setAvailableDivisions([]);
    setPointsOption("preset");
    setPresetPoints(3);
    setCustomPoints("");
    setReason("");
    setLeagueDayAdjustment("");
    setIsDisqualification(false);
    setDisqualificationReason("");
  };

  const handleRemove = async (id: string, type: "penalty" | "bonus") => {
    const confirmMessage =
      type === "bonus"
        ? "Are you sure you want to remove this bonus? The points will be deducted from the player."
        : "Are you sure you want to remove this penalty? The points will be restored to the player.";

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await apiRequest(`/api/penalties/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setSuccess(
          `${type === "bonus" ? "Bonus" : "Penalty"} removed successfully`,
        );
        fetchData();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to remove ${type}`);
      }
    } catch (error) {
      console.error(`Error removing ${type}:`, error);
      setError(`Failed to remove ${type}`);
    }
  };

  const handleRestorePlayer = async (playerId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to restore this player? They will reappear in the rankings.",
      )
    ) {
      return;
    }

    try {
      const response = await apiRequest(`/api/penalties/restore/${playerId}`, {
        method: "POST",
      });

      if (response.ok) {
        setSuccess("Player restored successfully");
        fetchData();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to restore player");
      }
    } catch (error) {
      console.error("Error restoring player:", error);
      setError("Failed to restore player");
    }
  };

  const openDialog = (type: "penalty" | "bonus") => {
    setDialogType(type);
    setDialogOpen(true);
    resetDialogState();
  };

  // Group penalties by player
  const playerPenalties = penalties.reduce(
    (acc, penalty) => {
      if (!acc[penalty.playerId]) {
        acc[penalty.playerId] = [];
      }
      acc[penalty.playerId].push(penalty);
      return acc;
    },
    {} as Record<string, PenaltyOrBonus[]>,
  );

  // Group bonuses by player
  const playerBonuses = bonuses.reduce(
    (acc, bonus) => {
      if (!acc[bonus.playerId]) {
        acc[bonus.playerId] = [];
      }
      acc[bonus.playerId].push(bonus);
      return acc;
    },
    {} as Record<string, PenaltyOrBonus[]>,
  );

  const playersWithPenalties = Object.keys(playerPenalties)
    .map((playerId) => {
      const player = players.find((p) => p.id === playerId);
      const playerPenaltyList = playerPenalties[playerId];

      // Find the most recent penalty date for this player
      const mostRecentPenaltyDate = playerPenaltyList.reduce(
        (latest, penalty) => {
          const penaltyDate = penalty.createdAt
            ? new Date(penalty.createdAt).getTime()
            : 0;
          return penaltyDate > latest ? penaltyDate : latest;
        },
        0,
      );

      return {
        playerId,
        playerName: player?.name || "Unknown Player",
        division: player?.division || "Division 1",
        isDisqualified: player?.isDisqualified || false,
        penalties: playerPenaltyList.sort((a, b) => {
          // Sort individual penalties by date (newest first)
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        }),
        totalPenaltyPoints: playerPenaltyList.reduce(
          (sum, p) => sum + p.points,
          0,
        ),
        mostRecentPenaltyDate,
      };
    })
    .sort((a, b) => b.mostRecentPenaltyDate - a.mostRecentPenaltyDate);

  const playersWithBonuses = Object.keys(playerBonuses)
    .map((playerId) => {
      const player = players.find((p) => p.id === playerId);
      return {
        playerId,
        playerName: player?.name || "Unknown Player",
        division: player?.division || "Division 1",
        bonuses: playerBonuses[playerId],
        totalBonusPoints: playerBonuses[playerId].reduce(
          (sum, p) => sum + p.points,
          0,
        ),
      };
    })
    .sort((a, b) => b.totalBonusPoints - a.totalBonusPoints);

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading data...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3
        }}
      >
        <Typography variant="h4">Penalties & Bonuses</Typography>
        <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
          <SeasonSelector fullWidth={false} excludeDrafts={true} />
          <DivisionSelector />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
        >
          <Tab label="⚠️ Penalties" />
          <Tab label="🏆 Bonuses" />
        </Tabs>
      </Box>

      {/* Penalties Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            color="error"
            startIcon={<WarningIcon />}
            onClick={() => openDialog("penalty")}
          >
            Add Penalty
          </Button>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Penalty Summary
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Overview of all players who have received penalties. Points are
              automatically deducted from player rankings.
            </Typography>

            {playersWithPenalties.length === 0 ? (
              <Alert severity="info">No penalties have been issued yet.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Player</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Division</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Penalties</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Date Applied</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Total Points Deducted</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {playersWithPenalties.map((playerData) => (
                      <TableRow
                        key={playerData.playerId}
                        sx={{
                          backgroundColor: playerData.isDisqualified
                            ? "rgba(211, 47, 47, 0.08)"
                            : "inherit",
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {playerData.playerName}
                            {playerData.isDisqualified && (
                              <Chip
                                label="DISQUALIFIED"
                                size="small"
                                color="error"
                                sx={{ fontWeight: "bold" }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={playerData.division} size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            {playerData.penalties.map((penalty) => (
                              <Box key={penalty.id} sx={{ width: "100%" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    justifyContent: "center",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {penalty.isDisqualification ? (
                                    <Chip
                                      label="Disqualified"
                                      size="small"
                                      color="error"
                                      icon={<WarningIcon />}
                                    />
                                  ) : (
                                    <>
                                      {penalty.points > 0 && (
                                        <Chip
                                          label={`-${penalty.points} pts`}
                                          size="small"
                                          color="error"
                                          icon={<WarningIcon />}
                                        />
                                      )}
                                      {penalty.leagueDayAdjustment &&
                                        penalty.leagueDayAdjustment > 0 && (
                                          <Chip
                                            label={`+${penalty.leagueDayAdjustment} days`}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                          />
                                        )}
                                    </>
                                  )}
                                </Box>
                                {penalty.reason && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      fontStyle: "italic",
                                      textAlign: "center",
                                      backgroundColor:
                                        "rgba(211, 47, 47, 0.05)",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {penalty.reason}
                                  </Typography>
                                )}
                                {penalty.disqualificationReason && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      fontStyle: "italic",
                                      textAlign: "center",
                                      backgroundColor:
                                        "rgba(211, 47, 47, 0.05)",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Reason: {penalty.disqualificationReason}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            {playerData.penalties.map((penalty) => (
                              <Typography
                                key={penalty.id}
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  textAlign: "center",
                                  py: 0.5,
                                }}
                              >
                                {penalty.createdAt
                                  ? new Date(
                                      penalty.createdAt,
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "N/A"}
                              </Typography>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {playerData.isDisqualified ? (
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color="error.main"
                            >
                              Excluded from Rankings
                            </Typography>
                          ) : (
                            <Typography
                              variant="body1"
                              fontWeight="bold"
                              color="error.main"
                            >
                              -{playerData.totalPenaltyPoints} points
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            {playerData.isDisqualified ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() =>
                                  handleRestorePlayer(playerData.playerId)
                                }
                              >
                                Restore Player
                              </Button>
                            ) : (
                              playerData.penalties.map((penalty) => (
                                <IconButton
                                  key={penalty.id}
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleRemove(penalty.id, "penalty")
                                  }
                                  title="Remove penalty"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              ))
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Bonuses Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            color="success"
            startIcon={<BonusIcon />}
            onClick={() => openDialog("bonus")}
          >
            Add Bonus
          </Button>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Bonus Summary
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Overview of all players who have received bonus points. Points are
              automatically added to player rankings.
            </Typography>

            {playersWithBonuses.length === 0 ? (
              <Alert severity="info">No bonuses have been awarded yet.</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Player</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Division</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Bonuses</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Total Bonus Points</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {playersWithBonuses.map((playerData) => (
                      <TableRow key={playerData.playerId}>
                        <TableCell>{playerData.playerName}</TableCell>
                        <TableCell align="center">
                          <Chip label={playerData.division} size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            {playerData.bonuses.map((bonus) => (
                              <Box key={bonus.id} sx={{ width: "100%" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    justifyContent: "center",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Chip
                                    label={`+${bonus.points} pts`}
                                    size="small"
                                    color="success"
                                    icon={<BonusIcon />}
                                  />
                                  {bonus.leagueDayAdjustment &&
                                    bonus.leagueDayAdjustment > 0 && (
                                      <Chip
                                        label={`-${bonus.leagueDayAdjustment} days`}
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                      />
                                    )}
                                </Box>
                                {bonus.reason && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      fontStyle: "italic",
                                      textAlign: "center",
                                      backgroundColor:
                                        "rgba(76, 175, 80, 0.05)",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {bonus.reason}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="success.main"
                          >
                            +{playerData.totalBonusPoints} points
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexWrap: "wrap",
                              justifyContent: "center",
                            }}
                          >
                            {playerData.bonuses.map((bonus) => (
                              <IconButton
                                key={bonus.id}
                                size="small"
                                color="error"
                                onClick={() => handleRemove(bonus.id, "bonus")}
                                title="Remove bonus"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Add Penalty/Bonus Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "penalty" ? "Add Penalty" : "Add Bonus"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Select Player</InputLabel>
              <Select
                value={selectedPlayer}
                label="Select Player"
                onChange={(e) => setSelectedPlayer(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a player</em>
                </MenuItem>
                {players
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((player) => (
                    <MenuItem key={player.id} value={player.id}>
                      {player.name} ({player.division}) - {player.totalPoints}{" "}
                      pts
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {selectedPlayer && (
              <FormControl fullWidth>
                <InputLabel>Division</InputLabel>
                <Select
                  value={selectedDivision}
                  label="Division"
                  onChange={(e) =>
                    setSelectedDivision(e.target.value as Division)
                  }
                  disabled={availableDivisions.length === 0}
                >
                  {availableDivisions.length > 0 ? (
                    availableDivisions.map((div) => (
                      <MenuItem key={div} value={div}>
                        {div}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={selectedDivision}>
                      {selectedDivision}
                    </MenuItem>
                  )}
                </Select>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  {availableDivisions.length > 0
                    ? dialogType === "penalty"
                      ? `Penalty will be deducted from ${selectedDivision} points`
                      : `Bonus will be added to ${selectedDivision} points`
                    : "Loading player divisions..."}
                </Typography>
              </FormControl>
            )}

            {dialogType === "penalty" && (
              <FormControl fullWidth>
                <InputLabel>Penalty Type</InputLabel>
                <Select
                  value={isDisqualification ? "disqualify" : "points"}
                  label="Penalty Type"
                  onChange={(e) => {
                    if (e.target.value === "disqualify") {
                      setIsDisqualification(true);
                    } else {
                      setIsDisqualification(false);
                    }
                  }}
                >
                  <MenuItem value="points">Points Deduction</MenuItem>
                  <MenuItem
                    value="disqualify"
                    sx={{ color: "error.main", fontWeight: "bold" }}
                  >
                    ⛔ Disqualify Player (Exclude from Rankings)
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {!isDisqualification && (
              <>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Points</FormLabel>
                  <RadioGroup
                    value={pointsOption}
                    onChange={(e) =>
                      setPointsOption(e.target.value as "preset" | "custom")
                    }
                  >
                    <FormControlLabel
                      value="preset"
                      control={<Radio />}
                      label="Preset Values"
                    />
                    <FormControlLabel
                      value="custom"
                      control={<Radio />}
                      label="Custom Value"
                    />
                  </RadioGroup>
                </FormControl>

                {pointsOption === "preset" ? (
                  <FormControl fullWidth>
                    <InputLabel>Select Points</InputLabel>
                    <Select
                      value={presetPoints}
                      label="Select Points"
                      onChange={(e) =>
                        setPresetPoints(e.target.value as number)
                      }
                    >
                      <MenuItem value={0}>0 Points (League Days Only)</MenuItem>
                      <MenuItem value={1}>1 Point</MenuItem>
                      <MenuItem value={3}>3 Points</MenuItem>
                      <MenuItem value={6}>6 Points</MenuItem>
                      <MenuItem value={10}>10 Points</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    type="number"
                    label="Custom Points"
                    value={customPoints}
                    onChange={(e) => setCustomPoints(e.target.value)}
                    inputProps={{ min: 0 }}
                    helperText="Enter any positive number (0 for league days only)"
                  />
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Reason"
                  placeholder={
                    dialogType === "penalty"
                      ? "Explain why this penalty is being applied..."
                      : "Explain why this bonus is being awarded..."
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                  error={!reason.trim()}
                  helperText={!reason.trim() ? "This field is required" : ""}
                />

                <TextField
                  fullWidth
                  type="number"
                  label={
                    dialogType === "penalty"
                      ? "League Days Penalty (optional)"
                      : "League Days Bonus (optional)"
                  }
                  placeholder={
                    dialogType === "penalty"
                      ? "Enter positive number to add payable days"
                      : "Enter positive number to reduce payable days"
                  }
                  value={leagueDayAdjustment}
                  onChange={(e) => setLeagueDayAdjustment(e.target.value)}
                  inputProps={{ min: 0 }}
                  helperText={
                    dialogType === "penalty"
                      ? "Add extra payable league days (e.g., 1 for late arrival)"
                      : "Reduce payable league days (e.g., 1 for helping as substitute)"
                  }
                />
              </>
            )}

            {isDisqualification && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Disqualification Reason"
                placeholder="Please provide a detailed reason for disqualifying this player..."
                value={disqualificationReason}
                onChange={(e) => setDisqualificationReason(e.target.value)}
                required
                error={!disqualificationReason.trim()}
                helperText={
                  !disqualificationReason.trim()
                    ? "This field is required for disqualification"
                    : "Explain why this player is being disqualified"
                }
              />
            )}

            {selectedPlayer && (
              <Alert
                severity={
                  isDisqualification
                    ? "error"
                    : dialogType === "penalty"
                      ? "warning"
                      : "success"
                }
                icon={
                  dialogType === "penalty" ? <WarningIcon /> : <BonusIcon />
                }
              >
                {isDisqualification ? (
                  <>
                    This will <strong>DISQUALIFY</strong>{" "}
                    <strong>
                      {players.find((p) => p.id === selectedPlayer)?.name}
                    </strong>{" "}
                    and{" "}
                    <strong>completely remove them from the rankings</strong>.
                    Their scores will not be counted. This action can be
                    reversed later.
                  </>
                ) : dialogType === "penalty" ? (
                  <>
                    This will deduct{" "}
                    <strong>
                      {pointsOption === "custom"
                        ? customPoints || "0"
                        : presetPoints}{" "}
                      points
                    </strong>{" "}
                    from{" "}
                    <strong>
                      {players.find((p) => p.id === selectedPlayer)?.name}
                    </strong>
                    's total score and affect their ranking immediately.
                  </>
                ) : (
                  <>
                    This will add{" "}
                    <strong>
                      {pointsOption === "custom"
                        ? customPoints || "0"
                        : presetPoints}{" "}
                      points
                    </strong>{" "}
                    to{" "}
                    <strong>
                      {players.find((p) => p.id === selectedPlayer)?.name}
                    </strong>
                    's total score and affect their ranking immediately.
                  </>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddPenaltyOrBonus}
            variant="contained"
            color={dialogType === "penalty" ? "error" : "success"}
            disabled={
              !selectedPlayer ||
              (isDisqualification && !disqualificationReason.trim()) ||
              (!isDisqualification && !reason.trim())
            }
          >
            {isDisqualification
              ? "Disqualify Player"
              : dialogType === "penalty"
                ? "Add Penalty"
                : "Add Bonus"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PenaltiesAndBonuses;
